import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import { SetupPageContainer } from "../../../utils/commonStyledComponents/commonStyled";
import { messages } from "../../../locales/messages";
import { useAnalyticsDashboard } from "../../../hooks/useAnalyticsDashboard";
import { useAnalyticsScope } from "../../../hooks/useAnalyticsScope";
import { DEFAULT_ANALYTICS_FILTERS } from "../../../utils/analytics/types";
import { AnalyticsFilterBar } from "./AnalyticsFilterBar";
import { AnalyticsQueryProvider } from "./AnalyticsQueryProvider";
import { AnalyticsDashboardGrid } from "./components/AnalyticsDashboardGrid";
import { AnalyticsUnavailableState } from "./components/AnalyticsUnavailableState";
import { analyticsTheme } from "./theme";
import { AnalyticsFilters } from "./types";

const AnalyticsPageContent = () => {
  const { t } = useTranslation();
  const { canUseFilters, hideMerchantAndStoreFilters } = useAnalyticsScope();

  const [filters, setFilters] = useState<AnalyticsFilters>(
    DEFAULT_ANALYTICS_FILTERS,
  );
  const [debouncedFilters, setDebouncedFilters] = useState<AnalyticsFilters>(
    DEFAULT_ANALYTICS_FILTERS,
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), 300);
    return () => clearTimeout(timer);
  }, [filters]);

  const {
    data,
    isPending,
    isLoading,
    isFetching,
    isUnavailable,
    isScopeViolation,
    isError,
    refetch,
  } = useAnalyticsDashboard(debouncedFilters);

  useEffect(() => {
    if (!isScopeViolation) return;
    setFilters((prev) => ({
      ...prev,
      merchantId: undefined,
      storeId: undefined,
      categoryId: undefined,
    }));
  }, [isScopeViolation]);

  const showLoading = isPending || isLoading || isFetching;
  const showError = isError && !isFetching;

  return (
    <SetupPageContainer data-testid="analytics-page">
      <Box
        sx={{
          width: "100%",
          backgroundColor: analyticsTheme.pageBg,
          minHeight: "calc(100vh - 65px)",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            maxWidth: 1320,
            mx: "auto",
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 3, md: 4 },
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: analyticsTheme.radius.md,
                  background: `linear-gradient(135deg, ${analyticsTheme.brand}, ${analyticsTheme.brandDark})`,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 6px 16px rgba(245,130,32,0.32)",
                  "& svg": { fontSize: 26 },
                }}
              >
                <InsightsRoundedIcon />
              </Box>
              <Box>
                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: 22, sm: 26 },
                    fontWeight: 800,
                    letterSpacing: -0.5,
                    lineHeight: 1.15,
                    color: analyticsTheme.text.primary,
                  }}
                >
                  {t(messages.Analytics_Title())}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: analyticsTheme.text.secondary,
                    mt: 0.25,
                  }}
                >
                  {t(messages.Analytics_Subtitle())}
                </Typography>
              </Box>
            </Box>

            <Tooltip title={t(messages.Refresh())}>
              <span>
                <IconButton
                  onClick={() => refetch()}
                  disabled={showLoading || isUnavailable}
                  data-testid="analytics-refresh"
                  sx={{
                    backgroundColor: analyticsTheme.cardBg,
                    border: `1px solid ${analyticsTheme.borderStrong}`,
                    borderRadius: analyticsTheme.radius.sm,
                    color: analyticsTheme.text.secondary,
                    transition: analyticsTheme.transition,
                    "&:hover": {
                      backgroundColor: analyticsTheme.brandSoft,
                      borderColor: analyticsTheme.brand,
                      color: analyticsTheme.brand,
                    },
                    "& svg": {
                      fontSize: 22,
                      animation: showLoading
                        ? "spin 0.9s linear infinite"
                        : "none",
                    },
                    "@keyframes spin": {
                      from: { transform: "rotate(0deg)" },
                      to: { transform: "rotate(360deg)" },
                    },
                  }}
                >
                  <RefreshRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {canUseFilters && (
            <AnalyticsFilterBar
              filters={filters}
              onFiltersChange={setFilters}
              hideMerchantAndStoreFilters={hideMerchantAndStoreFilters}
            />
          )}

          {isUnavailable ? (
            <AnalyticsUnavailableState
              message={t(messages.Analytics_Unavailable())}
            />
          ) : showError ? (
            <AnalyticsUnavailableState
              variant="error"
              message={t(
                messages.There_is_an_error_please_Contact_Administrator(),
              )}
            />
          ) : (
            <AnalyticsDashboardGrid data={data} isLoading={showLoading} />
          )}
        </Box>
      </Box>
    </SetupPageContainer>
  );
};

const AnalyticsPage = () => (
  <AnalyticsQueryProvider>
    <AnalyticsPageContent />
  </AnalyticsQueryProvider>
);

export default AnalyticsPage;
